import { useState, useCallback, useEffect } from 'react';
import { teamService } from '@/features/team/services/teamService/teamService';
import type { TeamMember, TeamStatus, InvitationAceptToken } from '@/features/team/types/team.types';
import type { AddUserFormValues, ApproveUserFormValues, EditUserFormValues, UserDetails } from '@/features/team/types/team.types';
import { useSpinner, useToast } from '@chikara/ui';
import { useAsyncGuard } from '@chikara/hooks';
import { t } from '@/i18n';


export function useTeam() {
  const { show, hide } = useSpinner();
  const { success } = useToast();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const { checkGuard, resetGuard } = useAsyncGuard();

  const fetchMembers = useCallback(async (page: number = 1, limit: number = 10) => {
    if (!checkGuard()) return;

    show();
    setIsLoading(true);
    try {
      const { data } = await teamService.getListMembers(page, limit);
      const mapTeamData = (data: any) =>
        data.map((item: any) => ({
          uuid: item.user_uuid,
          email: item.email,
          fullname: item.full_name,
          disposable: item.balance?.disposable_balance || 0,
          monthly_limit: item.balance?.limit_balance || 0,
          role: item.role,
          status: item.status,
          _member: item,
        }));
      setMembers(mapTeamData(data.data));
      setPaginationData(prev => ({
        ...prev,
        currentPage: data.page,
        totalPages: data.totalPages,
        itemsPerPage: data.limit,
        totalItems: data.total,
      }));
      setError(null);
    } catch (err) {
      console.error(err);
      resetGuard();
    } finally {
      hide();
      setIsLoading(false);
    }
  }, [show, hide, checkGuard, resetGuard]);

  const handlePageChange = useCallback((page: number, limit: number) => {
    resetGuard();
    fetchMembers(page, limit);
  }, [fetchMembers, resetGuard]);

  const pagination = {
    ...paginationData,
    onPageChange: handlePageChange
  };

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const createMember = useCallback(async (values: AddUserFormValues) => {
    show();
    try {
      await teamService.createMember(values);
      success(t('team.response.createMember'));
      resetGuard();
      await fetchMembers(paginationData.currentPage, paginationData.itemsPerPage);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      hide();
    }
  }, [fetchMembers, paginationData, show, hide, success, resetGuard]);

  const inviteAccept = useCallback(async (values: InvitationAceptToken) => {
    show();
    try {
      await teamService.inviteAccept(values);
      resetGuard();
      await fetchMembers(paginationData.currentPage, paginationData.itemsPerPage);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      hide();
    }
  }, [fetchMembers, paginationData, show, hide, resetGuard]);

  const approveMember = useCallback(async (member_uuid: string, values: ApproveUserFormValues, type: TeamStatus) => {
    show();
    const request: ApproveUserFormValues = {
      status: type,
      monthly_limit: values.monthly_limit,
      account_uuid: values.account_uuid,
    }

    try {
      await teamService.approveMember(request, member_uuid);
      success(t(`team.response.${type}Member`));
      resetGuard();
      await fetchMembers(paginationData.currentPage, paginationData.itemsPerPage);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      hide();
    }
  }, [fetchMembers, paginationData, show, hide, success, resetGuard]);

  const updateMember = useCallback(async (member_uuid: string, values: EditUserFormValues) => {
    show();
    try {
      await teamService.updateMember(values, member_uuid);
      success(t('team.response.updateMember'));
      resetGuard();
      await fetchMembers(paginationData.currentPage, paginationData.itemsPerPage);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      hide();
    }
  }, [fetchMembers, paginationData, show, hide, success, resetGuard]);

  const cancelMember = useCallback(async (member_uuid: string) => {
    show();
    try {
      await teamService.cancelMember(member_uuid);
      success(t('team.response.cancelMember'));
      resetGuard();
      await fetchMembers(paginationData.currentPage, paginationData.itemsPerPage);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      hide();
    }
  }, [fetchMembers, paginationData, show, hide, success, resetGuard]);

  const getMember = useCallback(async (member_uuid: string, allData?: boolean): Promise<UserDetails | null> => {
    show();
    try {
      const userData = await teamService.getMember(member_uuid);

      const userDetails: UserDetails = {
        uuid: userData.uuid,
        customer_uuid: userData.customer_uuid,
        email: userData.email,
        name: userData.name,
        lname: userData.lname,
        slname: userData.slname,
        role: userData.role as any,
        monthly_limit: userData.monthly_limit,
        phone: userData.phone || '',
        account: userData.account,
        status: userData.status
      }

      if (allData) {
        return userData;
      }

      return {
        ...userDetails
      } as UserDetails;

    } catch (error) {
      console.error('Failed to fetch user data for approval', error);
      return null;
    } finally {
      hide();
    }
  }, [show, hide]);

  return {
    members,
    pagination,
    isLoading,
    error,
    createMember,
    inviteAccept,
    approveMember,
    updateMember,
    cancelMember,
    getMember,
    refresh: (page?: number, limit?: number) => {
      resetGuard();
      return fetchMembers(page, limit);
    },
  };
}
